import { useState, useCallback, useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { INITIAL_NUTRITIONAL_FILTER_STATE, FILTER_MAPPINGS, INITIAL_CATEGORIES, FILTER_STICKY_DURATION } from '../../settings';
import { useAuth0 } from "@auth0/auth0-react";
import * as Constants from '../../components/Constants';

const STORAGE_KEY = 'recipe_filters';
const UPDATE_DEBOUNCE_MS = 800;

export const useFilterState = () => {
  const { user } = useAuth0();
  const { setIndexUiState, indexUiState } = useInstantSearch();
  
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTimeoutRef = useRef(null);

  // Load initial state from localStorage with timestamp-based expiry
  const loadInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Check if the saved state has a timestamp and if it's still valid
        if (parsed.timestamp) {
          const currentTime = new Date().getTime();
          const timeSinceLastSave = currentTime - parsed.timestamp;
          
          if (timeSinceLastSave > FILTER_STICKY_DURATION) {
            // State has expired, remove it and return defaults
            localStorage.removeItem(STORAGE_KEY);
            return getDefaultState();
          }
        }
        
        const loadedState = {
          nutritionalFilters: parsed.nutritionalFilters || INITIAL_NUTRITIONAL_FILTER_STATE,
          categoryFilters: parsed.categoryFilters || INITIAL_CATEGORIES,
          searchQuery: indexUiState?.query || parsed.searchQuery || '',
          onlyShowMyRecipes: parsed.onlyShowMyRecipes !== undefined ? parsed.onlyShowMyRecipes : false,
          showSearchFilters: parsed.showSearchFilters || false,
        };
        return loadedState;
      }
    } catch (error) {
      console.error('Error loading filter state:', error);
      // Return defaults on error
      return getDefaultState();
    }
    return getDefaultState();
  };

  // Helper function to get default state
  const getDefaultState = () => {
    return {
      nutritionalFilters: INITIAL_NUTRITIONAL_FILTER_STATE,
      categoryFilters: INITIAL_CATEGORIES,
      searchQuery: indexUiState?.query || '',
      onlyShowMyRecipes: false, // Default to "Show everyone's recipes"
      showSearchFilters: false,
    };
  };

  const initialState = loadInitialState();
  
  // Initialize state
  const [nutritionalFilters, setNutritionalFilters] = useState(initialState.nutritionalFilters);
  const [categoryFilters, setCategoryFilters] = useState(initialState.categoryFilters);
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [onlyShowMyRecipes, setOnlyShowMyRecipes] = useState(initialState.onlyShowMyRecipes);
  const [showSearchFilters, setShowSearchFilters] = useState(initialState.showSearchFilters);

  // Build the complete filter query
  const buildFilterQuery = useCallback((filters = {}) => {
    const {
      nutritionalFiltersOverride = nutritionalFilters,
      categoryFiltersOverride = categoryFilters,
      onlyShowMyRecipesOverride = onlyShowMyRecipes
    } = filters;

    const filterParts = [];
    
    // Add nutritional filters
    const nutritionalFilterQueries = Object.entries(nutritionalFiltersOverride)
      .filter(([, value]) => value)
      .map(([key]) => FILTER_MAPPINGS[key]);
    
    if (nutritionalFilterQueries.length > 0) {
      filterParts.push(`(${nutritionalFilterQueries.join(' AND ')})`);
    }
    
    // Add category filters
    const selectedCategories = categoryFiltersOverride
      .filter(cat => cat.checked)
      .map(cat => `category:"${cat.label}"`);
    
    if (selectedCategories.length > 0) {
      filterParts.push(`(${selectedCategories.join(' OR ')})`);
    }
    // If no categories selected, show all (don't add any filter)
    
    // Add my recipes filter
    if (onlyShowMyRecipesOverride && user) {
      filterParts.push(`userid:"${Constants.encodeUser(user)}"`);
    }
    
    return filterParts.length > 0 ? filterParts.join(' AND ') : '';
  }, [nutritionalFilters, categoryFilters, onlyShowMyRecipes, user]);

  // Helper function to save state to localStorage with timestamp
  const saveState = useCallback(() => {
    const state = {
      nutritionalFilters,
      categoryFilters,
      searchQuery,
      onlyShowMyRecipes,
      showSearchFilters,
      timestamp: new Date().getTime(), // Add timestamp for expiry tracking
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [nutritionalFilters, categoryFilters, searchQuery, onlyShowMyRecipes, showSearchFilters]);

  // Save state whenever any filter state changes (but not on initial load)
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    if (hasInitialized) {
      saveState();
    } else {
      setHasInitialized(true);
    }
  }, [nutritionalFilters, categoryFilters, searchQuery, onlyShowMyRecipes, showSearchFilters, saveState, hasInitialized]);

  // Helper function to handle Algolia updates and loading states
  const updateWithLoading = useCallback((updateFn) => {
    setIsUpdating(true);
    updateFn();
    
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      setIsUpdating(false);
    }, UPDATE_DEBOUNCE_MS);
  }, []);

  // Update search query
  const updateSearchQuery = useCallback((query) => {
    const normalizedQuery = query || '';
    setSearchQuery(normalizedQuery);
    
    updateWithLoading(() => {
      // When clearing the search, ensure we reset the query in Algolia
      if (!normalizedQuery) {
        setIndexUiState(state => ({
          ...state,
          page: 0,
          query: undefined, // This ensures Algolia properly resets the search
          filters: buildFilterQuery()
        }));
      } else {
        setIndexUiState(state => ({
          ...state,
          page: 0,
          query: normalizedQuery,
          filters: buildFilterQuery()
        }));
      }
    });
  }, [setIndexUiState, buildFilterQuery, updateWithLoading]);

  // Toggle functions
  const toggleCategoryFilter = useCallback((categoryLabel) => {
    const newCategoryFilters = categoryFilters.map(cat => 
      cat.label === categoryLabel 
        ? { ...cat, checked: !cat.checked }
        : cat
    );
    setCategoryFilters(newCategoryFilters);
    
    updateWithLoading(() => {
      setIndexUiState(state => ({
        ...state,
        filters: buildFilterQuery({ categoryFiltersOverride: newCategoryFilters })
      }));
    });
  }, [setIndexUiState, buildFilterQuery, categoryFilters, updateWithLoading]);

  const toggleNutritionalFilter = useCallback((filterKey) => {
    const newNutritionalFilters = {
      ...nutritionalFilters,
      [filterKey]: !nutritionalFilters[filterKey]
    };
    setNutritionalFilters(newNutritionalFilters);
    
    updateWithLoading(() => {
      setIndexUiState(state => ({
        ...state,
        filters: buildFilterQuery({ nutritionalFiltersOverride: newNutritionalFilters })
      }));
    });
  }, [setIndexUiState, buildFilterQuery, nutritionalFilters, updateWithLoading]);

  const toggleOnlyShowMyRecipes = useCallback((value) => {
    setOnlyShowMyRecipes(value);
    
    updateWithLoading(() => {
      setIndexUiState(state => ({
        ...state,
        filters: buildFilterQuery({ onlyShowMyRecipesOverride: value })
      }));
    });
  }, [setIndexUiState, buildFilterQuery, updateWithLoading]);

  const toggleSearchFilters = useCallback(() => {
    setShowSearchFilters(prev => !prev);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return {
    searchQuery,
    nutritionalFilters,
    categoryFilters,
    onlyShowMyRecipes,
    showSearchFilters,
    isUpdating,
    updateSearchQuery,
    toggleCategoryFilter,
    toggleNutritionalFilter,
    toggleOnlyShowMyRecipes,
    toggleSearchFilters,
    buildFilterQuery,
  };
}; 